import catalog from './catalog.json';

export const TV_INSTALL_URL = 'https://www.telegames.ai/tv/install';
export const TV_RECEIVER_ORIGIN = 'https://beta.telegames.ai';
export const APK_PLATFORMS = ['android-tv', 'fire-os'] as const;
export type ApkPlatform = typeof APK_PLATFORMS[number];
export type ApkRelease = {
  status: 'release'; platform: ApkPlatform; applicationId: 'ai.telegames.tv';
  version: string; versionCode: number; bytes: number; sha256: string;
  signerSha256: string; signingVerified: true; debuggable: false;
  receiverOrigin: string; verifiedAt: string; downloadPath: string;
};
const digest = (value: unknown): value is string => typeof value === 'string' && /^[a-f0-9]{64}$/.test(value);
const dated = (value: unknown): value is string => typeof value === 'string' && Number.isFinite(Date.parse(value));
// Mirrors RELEASE_VERSION in packages/tv-receiver/scripts/prepare-apk-release.mjs: X.Y.Z, X.Y.Z-rc.N, X.Y.Z-rcN.M[.P[.Q]] (#4026).
const releaseVersion = /^\d+\.\d+\.\d+(?:-rc(?:\.\d+|\d+(?:\.\d+){1,3}))?$/;
export function approvedApk(platform: string, input: unknown = catalog): ApkRelease | null {
  if (!APK_PLATFORMS.includes(platform as ApkPlatform)) return null;
  const data = input as { schemaVersion?: number; receiverOrigin?: string; releases?: Record<string, ApkRelease | null> };
  if (data?.schemaVersion !== 1 || data.receiverOrigin !== TV_RECEIVER_ORIGIN) return null;
  const item = data.releases?.[platform];
  if (!item || item.platform !== platform || item.status !== 'release' || item.applicationId !== 'ai.telegames.tv' ||
      item.signingVerified !== true || item.debuggable !== false || item.receiverOrigin !== TV_RECEIVER_ORIGIN ||
      !digest(item.sha256) || !digest(item.signerSha256) || !dated(item.verifiedAt) ||
      typeof item.version !== 'string' || !releaseVersion.test(item.version) ||
      !Number.isSafeInteger(item.versionCode) || item.versionCode < 1 || !Number.isSafeInteger(item.bytes) || item.bytes < 1 ||
      item.downloadPath !== `/tv/releases/${platform}/${item.sha256}.apk`) return null;
  return item;
}
export function verifiedDownloaderCode(input: unknown = catalog): string | null {
  const item = (input as { downloader?: { code?: string; shortUrl?: string; destination?: string; verifiedAt?: string } })?.downloader;
  return item && typeof item.code === 'string' && /^\d{4,10}$/.test(item.code) && item.destination === TV_INSTALL_URL && item.shortUrl === `https://aftv.news/${item.code}` && dated(item.verifiedAt) ? item.code : null;
}
export function vegaStoreUrl(input: unknown = catalog): string | null {
  const item = (input as { vega?: { status?: string; storeUrl?: string; verifiedAt?: string } })?.vega;
  if (item?.status !== 'published' || !dated(item.verifiedAt) || typeof item.storeUrl !== 'string') return null;
  try {
    const url = new URL(item.storeUrl);
    return url.protocol === 'https:' && url.hostname === 'www.amazon.com' && /^\/dp\/[A-Z0-9]{10}$/.test(url.pathname) && !url.search && !url.hash && !url.username && !url.password && !url.port ? url.href : null;
  } catch { return null; }
}
/**
 * Prototype environments only (Panda): show the Room TV control even though no TV app is released,
 * because the receiver there is the browser receiver page (`/tv/index.html`) or a tester package,
 * not a store install. Build-time (`NEXT_PUBLIC_*`), so it cannot be flipped on a deployed beta by
 * accident; beta's build never sets it. The TV App install section keeps its own gate: with no app
 * there is still nothing to install.
 */
export function tvRoomControlWithoutApp(): boolean {
  // Written out in full on purpose: Next inlines `process.env.NEXT_PUBLIC_*` into the client bundle
  // only when the property access is literal; `process.env` as an object is empty in the browser.
  return process.env.NEXT_PUBLIC_TV_ROOM_CONTROL_WITHOUT_APP === 'true';
}
/**
 * One install URL and one Downloader code for every environment. The TV apps default to beta and
 * staff switch a TV to Panda on the device, so a prototype environment never serves its own
 * install page or downloads: it forwards to the public install URL (#4026 catalog stays beta's).
 */
export function tvInstallServedElsewhere(): boolean {
  return tvRoomControlWithoutApp();
}
/** An approved APK or a published Vega listing exists. Until then there is nothing to install, so TV entry points stay hidden (#4026). */
export function tvAppAvailable(input: unknown = catalog): boolean {
  return APK_PLATFORMS.some(platform => approvedApk(platform, input) !== null) || vegaStoreUrl(input) !== null;
}
